import CalendarDaily from '@/features/calendar/components/CalendarDaily/CalendarDaily';
import CalendarPeriodSelector from '@/features/calendar/components/CalendarPeriodSelector';
import { useCalendarApi } from '@/features/calendar/hooks/useCalendarApi';
import ReservationDetails from '@/features/reservations/components/ReservationDetails';
import { endOfMonth, startOfMonth } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentType } from '@/features/calendar/types/calendar-dto';
import CalendarMonthlySingle from '@/features/calendar/components/CalendarMonthlySingle';
import CalendarApartmentSelector from '@/features/calendar/components/CalendarApartmentSelector';
import { cn } from '@/lib/utils';
import ReservationManage from '@/features/reservations/components/ReservationManage';

type Props = {
	id: string;
	isMobile?: boolean;
};

const OwnerDetailsListCalendar: FC<Props> = ({ id, isMobile }) => {
	const [apartmentId, setApartmentId] = useState('');
	const [selectedDate, setSelectedDate] = useState(new Date());
	const [searchText, setSearchText] = useState('');
	const [reservationId, setReservationId] = useState('');
	const [reservationApartmentId, setReservationApartmentId] = useState('');
	const [reservationDetailsApartmentId, setReservationDetailsApartmentId] = useState('');
	const [reservationType, setReservationType] = useState<DocumentType>();
	const [reservationCheckIn, setReservationCheckIn] = useState<Date>();
	const [reservationCheckOut, setReservationCheckOut] = useState<Date>();
	const [showReservationDetails, setShowReservationDetails] = useState(false);
	const [showReservationManage, setShowReservationManage] = useState(false);

	const { t } = useTranslation();

	const { calendarEventsListData, isFetchingCalendarEventsListData } =
		useCalendarApi({
			startDate: startOfMonth(selectedDate),
			endDate: endOfMonth(selectedDate),
			searchText,
			ownerId: id,
			apartmentId,
		});

	const handleShowReservationDetails = (
		id: string,
		type: DocumentType,
		apartmentId?: string,
		checkInDate?: Date,
		checkOutDate?: Date,
	) => {
		setReservationId(id);
		setApartmentId(apartmentId || '');
		setReservationDetailsApartmentId(apartmentId || '');
		setReservationType(type);
		setReservationCheckIn(checkInDate);
		setReservationCheckOut(checkOutDate);
		setShowReservationDetails(true);
	};

	const handleShowEditReservation = (id: string) => {
		setShowReservationDetails(false);
		setReservationApartmentId(id);
		setShowReservationManage(true);
	};

	return (
		<div className='h-full flex flex-col gap-4 select-none'>
			<div className='flex items-center justify-between gap-2'>
				<h3 className={cn(isMobile && 'text-base')}>
					{t('main.owners.components.details.list.calendar.title')}
				</h3>
				{isMobile ? (
					<CalendarApartmentSelector
						selectedDate={selectedDate}
						setApartmentId={setApartmentId}
					/>
				) : (
					<CalendarPeriodSelector
						viewMode={'month'}
						selectedDate={selectedDate}
						setSelectedDate={setSelectedDate}
					/>
				)}
			</div>
			{isMobile ? (
				<CalendarMonthlySingle
					apartments={calendarEventsListData?.apartments}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					isLoading={isFetchingCalendarEventsListData}
					showReservationDetails={handleShowReservationDetails}
				/>
			) : (
				<CalendarDaily
					apartments={calendarEventsListData?.apartments}
					selectedDate={selectedDate}
					searchText={searchText}
					setSearchText={setSearchText}
					showReservationDetails={handleShowReservationDetails}
					headerWidth={'50'}
				/>
			)}
			{showReservationDetails && (
				<ReservationDetails
					id={reservationId}
					documentType={reservationType}
					apartmentId={reservationDetailsApartmentId}
					startDate={reservationCheckIn}
					endDate={reservationCheckOut}
					onEdit={handleShowEditReservation}
					onClose={() => setShowReservationDetails(false)}
					isMobile={isMobile}
				/>
			)}
			{showReservationManage && (
				<ReservationManage
					apartmentId={reservationApartmentId}
					reservationId={reservationId}
					onClose={() => setShowReservationManage(false)}
				/>
			)}
		</div>
	);
};

export default OwnerDetailsListCalendar;
